import { analyticsRepository } from "@/repositories/analytics";
import { useStore } from "@/stores/useStore";
import { useCallback } from "react";
import {
  ActivityFeedFilterAppliedEvent,
  ActivityFeedLoadMoreEvent,
  ANALYTICS_EVENTS,
} from "@/types/v2/Analytics";
import { ActivityFeedSearchFilters } from "./useActivityFeedPagination";

/**
 * Hook for tracking Activity Feed analytics events.
 *
 * Provides methods to track user interactions with the Activity Feed
 * including filter applications and pagination usage. All tracking
 * follows existing analytics patterns and includes proper error handling.
 *
 * @example
 * ```typescript
 * const { trackFilterApplied, trackLoadMore } = useActivityFeedAnalytics();
 *
 * // Track when user applies filters
 * trackFilterApplied({ activityType: "note_added", cardNumber: "12345" });
 *
 * // Track when user loads more items
 * trackLoadMore(2, 40, true, 250);
 * ```
 */
export const useActivityFeedAnalytics = () => {
  const shopId = useStore((s) => s.shopId);
  const userId = useStore((s) => s.user?.uid);

  /**
   * Wrapper function for analytics events that adds common parameters
   * and handles missing shop/user context gracefully.
   */
  const analyticsWrapper = useCallback(
    (
      eventName: string,
      eventParams: Omit<
        ActivityFeedFilterAppliedEvent | ActivityFeedLoadMoreEvent,
        "shopId" | "userId" | "timestamp"
      >
    ) => {
      if (!shopId) {
        console.error(
          "Shop ID is missing, cannot log analytics event:",
          eventName
        );
        return;
      }

      if (!userId) {
        console.error(
          "User ID is missing, cannot log analytics event:",
          eventName
        );
        return;
      }

      try {
        analyticsRepository.logAnalyticsEvent(eventName, {
          ...eventParams,
          shopId,
          userId,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.error(
          "Failed to log Activity Feed analytics event:",
          eventName,
          error
        );
      }
    },
    [shopId, userId]
  );

  /**
   * Tracks when a user applies filters to the Activity Feed.
   *
   * Analyzes the filter parameters to determine the filter type
   * and captures all relevant filter values for understanding
   * user search and navigation patterns.
   *
   * @param filters - The filter parameters being applied
   *
   * @example
   * ```typescript
   * // Track activity type filter
   * trackFilterApplied({ activityType: "note_added" });
   *
   * // Track card number search
   * trackFilterApplied({ cardNumber: "WO-12345" });
   *
   * // Track date range filter
   * trackFilterApplied({
   *   startDate: new Date("2024-01-01"),
   *   endDate: new Date("2024-01-31")
   * });
   *
   * // Track combination of filters
   * trackFilterApplied({
   *   activityType: "container_movement",
   *   cardNumber: "WO-12345",
   *   startDate: new Date("2024-01-01")
   * });
   * ```
   */
  const trackFilterApplied = useCallback(
    (filters: ActivityFeedSearchFilters) => {
      // Determine filter type based on what filters are present
      let filterType: ActivityFeedFilterAppliedEvent["filterType"];
      const hasActivityType = filters.activityType !== undefined;
      const hasCardNumber = !!filters.cardNumber?.trim();
      const hasDateRange = !!(filters.startDate || filters.endDate);

      const activeFiltersCount = [
        hasActivityType,
        hasCardNumber,
        hasDateRange,
      ].filter(Boolean).length;

      if (activeFiltersCount > 1) {
        filterType = "combination";
      } else if (hasActivityType) {
        filterType = "activityType";
      } else if (hasCardNumber) {
        filterType = "cardNumber";
      } else if (hasDateRange) {
        filterType = "dateRange";
      } else {
        // No filters applied, this might be a reset
        filterType = "activityType"; // Default for when clearing filters
      }

      const eventParams: Omit<
        ActivityFeedFilterAppliedEvent,
        "shopId" | "userId" | "timestamp"
      > = {
        filterType,
        hasDateRange,
      };

      // Add optional parameters only if they exist
      if (filters.activityType !== undefined) {
        eventParams.activityType = filters.activityType;
      }

      if (filters.cardNumber?.trim()) {
        eventParams.cardNumber = filters.cardNumber.trim();
      }

      if (filters.startDate) {
        eventParams.startDate = filters.startDate.toISOString();
      }

      if (filters.endDate) {
        eventParams.endDate = filters.endDate.toISOString();
      }

      analyticsWrapper(
        ANALYTICS_EVENTS.ACTIVITY_FEED_FILTER_APPLIED,
        eventParams
      );
    },
    [analyticsWrapper]
  );

  /**
   * Tracks when a user loads more items in the Activity Feed pagination.
   *
   * Captures pagination engagement metrics including page progression,
   * filter context, and performance data to understand user engagement
   * depth and optimize loading strategies.
   *
   * @param currentPage - The page number being loaded (1-based)
   * @param totalItemsLoaded - Total number of items loaded so far
   * @param hasActiveFilters - Whether any filters are currently applied
   * @param loadTimeMs - Optional loading time in milliseconds for performance tracking
   * @param pageSize - Number of items per page (defaults to 20)
   *
   * @example
   * ```typescript
   * // Track basic pagination
   * trackLoadMore(2, 40, false);
   *
   * // Track pagination with filters and performance data
   * trackLoadMore(3, 60, true, 350, 20);
   *
   * // Track pagination with performance timing
   * const startTime = performance.now();
   * await loadMoreData();
   * const loadTime = performance.now() - startTime;
   * trackLoadMore(currentPage, totalItems, hasFilters, loadTime);
   * ```
   */
  const trackLoadMore = useCallback(
    (
      currentPage: number,
      totalItemsLoaded: number,
      hasActiveFilters: boolean,
      loadTimeMs?: number,
      pageSize: number = 20
    ) => {
      const eventParams: Omit<
        ActivityFeedLoadMoreEvent,
        "shopId" | "userId" | "timestamp"
      > = {
        currentPage,
        totalItemsLoaded,
        pageSize,
        hasActiveFilters,
      };

      // Add optional load time if provided
      if (loadTimeMs !== undefined) {
        eventParams.loadTimeMs = Math.round(loadTimeMs);
      }

      analyticsWrapper(ANALYTICS_EVENTS.ACTIVITY_FEED_LOAD_MORE, eventParams);
    },
    [analyticsWrapper]
  );

  return {
    trackFilterApplied,
    trackLoadMore,
  };
};
